import {
    ReactElement,
    useEffect,
} from 'react';

import {
    Center,
    Checkbox,
    SimpleGrid,
    Table,
} from '@mantine/core';

import {useTopLevelDomains} from '../hooks/useTopLevelDomains';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    SimpleTablePagination,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {useTopLevelDomainsSelection} from '../hooks/useTopLevelDomainsSelection';
import {
    ITopLevelDomainTableColumn,
    TopLevelDomain,
} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {NavLink} from "react-router-dom";
import {I18n} from 'aws-amplify/utils';
import {formatTimestamp} from "../../../utils/TableTimestamps.tsx";

export const RecordsTable = ({
                                 extraTools,
                                 settings,
                             }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[]
}) => {

    const {allowedActions} = useVerifiedPermissions()

    const {
        user
    } = useAuth();


    if (!user) {
        throw new Error('user is required parameter')
    }

    const cognitoUsername = user.username;


    const {
        visibleItems,
        limit,
        loading,
        totalPages,
        visibleCount,
        fetchRecords,
        handleFilterByKeyword,
        handleItemsPerPageChange,
        handlePageChange,
    } = useTopLevelDomains();


    const {
        selection,
        handleRowSelection,
        handleSelectAllRows,
    } = useTopLevelDomainsSelection();

    useEffect(() => {
        fetchRecords();
    }, []);


    const getColumns = () => {
        let cols: ITopLevelDomainTableColumn[] = [];
        if (allowedActions.includes('DeleteTopLevelDomain')) {

            cols.push({
                          id: 'select',
                          title: '',
                          render: item => {
                              if (item.createdByUsername === cognitoUsername) {
                                  return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                              } else {
                                  return (<Center><Checkbox disabled={true}></Checkbox></Center>);
                              }

                          },


                      },
            );
        }


        cols.push({
                      id: 'name',
                      title: 'Name',
                      render: item => {
                          return (allowedActions.includes('EditTopLevelDomain')) ? (
                              <NavLink to={`/top-level-domains/${item.id}`}>{item.name}</NavLink>) : item.name
                      },
                  });

        cols.push({
                      id: 'type',
                      title: 'TLD Type',
                      render: item => {
                          return item.tldType
                      },
                  });

        if ((allowedActions.includes('EditTopLevelDomain'))) {
            cols.push({
                          title: I18n.get('Manager'),
                          width: '1fr',
                          render: (item: TopLevelDomain) => item.tldManager,
                      }
            );

            cols.push({
                          title: I18n.get('Created'),
                          width: '1fr',
                          render: (item: TopLevelDomain) => formatTimestamp(item.created as string),
                      }
            );
        }


        return cols;


    };


    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={limit}
                handleItemsPerPageChange={handleItemsPerPageChange}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumns() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumns() as TableBodyColumn[]}
                />
            </Table>

            <SimpleTablePagination
                totalPages={totalPages}
                visibleCount={visibleCount}
                handlePageChange={handlePageChange}
            />
        </SimpleGrid>
    );
};
